import { AHButton, AHSeparator, Box, useLayoutContext, useTheme } from "arkh";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { ScrollView } from "react-native-gesture-handler";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useKeyboardInModal } from "../../Files/useKeyboardInModal";
import { PLUCategoryIdentifiers } from "../PLUCategoryIdentifiers";
import { PLUCategoryEditDeleteMutation } from "./__generated__/PLUCategoryEditDeleteMutation.graphql";
import { PLUCategoryEditMutation } from "./__generated__/PLUCategoryEditMutation.graphql";
import { PLUCategoryEditQuery } from "./__generated__/PLUCategoryEditQuery.graphql";

import { TeamSelectListItem } from "widgets/src/TeamSelectList";

import { useAlert } from "../../../common/Alert";
import { useNavigationHeaderOptions } from "../../../common/navigationHeaderOptions";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { TeamSelectListItem_Team$key } from "widgets/src/__generated__/TeamSelectListItem_Team.graphql";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"PLUCategoryEdit">;
  route: UserLoggedInNavigatorRouteProp<"PLUCategoryEdit">;
};
export const PLUCategoryEdit = withWebModalStack(PLUCategoryEditC);
function PLUCategoryEditC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerPLUCategoryEdit fetchKey={fetchKey} {...props} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerPLUCategoryEdit(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<PLUCategoryEditQuery>(
    graphql`
      query PLUCategoryEditQuery($id: ID!) {
        pluCategory(id: $id) {
          id
          color
          title
          teamsFilter {
            id
          }
        }
        store {
          publicTeams {
            id
            type
            ...TeamSelectListItem_Team
          }
        }
        pluCategories {
          id
          color
        }
      }
    `,
    {
      id: props.route.params.id,
    },
    { fetchKey: props.fetchKey }
  );

  const usedColors = useMemo(() => {
    return data.pluCategories
      .filter((category) => category.id !== data.pluCategory?.id)
      .map((category) => category.color);
  }, [data.pluCategories, data.pluCategory?.id]);

  return data.pluCategory ? (
    <Component
      {...props}
      usedColors={usedColors}
      remoteColor={data.pluCategory.color}
      remoteTitle={data.pluCategory.title}
      checkoutTeams={data.store.publicTeams.filter(
        (t) => t.type === "CHECKOUT"
      )}
      remoteFilteredTeamIds={data.pluCategory.teamsFilter.map((t) => t.id)}
    />
  ) : null;
}

function Component(
  props: Props & {
    remoteColor: string;
    remoteTitle: string;
    usedColors: string[];
    checkoutTeams: (TeamSelectListItem_Team$key & { readonly id: string })[];
    remoteFilteredTeamIds: string[];
  }
) {
  const intl = useIntl();
  const intlTokens = useIntlTokens();
  const [keyboardBottomInset, { onLayout }] = useKeyboardInModal();
  const theme = useTheme();
  const layout = useLayoutContext();

  const [color, setColor] = useState(props.remoteColor);
  const [title, setTitle] = useState(props.remoteTitle);
  const [filteredTeamIds, setFilteredTeamIds] = useState(
    props.remoteFilteredTeamIds
  );

  const [deletePLUCategory, isDeleting] =
    useMutation<PLUCategoryEditDeleteMutation>(graphql`
      mutation PLUCategoryEditDeleteMutation($id: ID!) {
        pluCategory(id: $id) {
          delete {
            pluCategories {
              id
              ...PLUCategoryItem_PLUCategory
            }
          }
        }
      }
    `);

  const [updatePLUCategory, isInFlight] = useMutation<PLUCategoryEditMutation>(
    graphql`
      mutation PLUCategoryEditMutation(
        $id: ID!
        $input: PLUUpdateCategoryInput!
      ) {
        pluCategory(id: $id) {
          update(input: $input) {
            id
            title
            color
            teamsFilter {
              id
            }
          }
        }
      }
    `
  );
  const onSave = useCallback(() => {
    updatePLUCategory({
      variables: {
        id: props.route.params.id,
        input: {
          setColor: color,
          setTitle: title,
          setFilterTeamIds: filteredTeamIds,
        },
      },
      onCompleted: props.navigation.goBack,
    });
  }, [
    updatePLUCategory,
    props.route.params.id,
    props.navigation.goBack,
    color,
    title,
    filteredTeamIds,
  ]);

  const getOptions = useNavigationHeaderOptions();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "SAVE",
        onSave,
        onCancel: props.navigation.goBack,
        saveLoading: isInFlight,
        title: "",
      })
    );
  }, [
    getOptions,
    isInFlight,
    onSave,
    props.navigation,
    theme.primary,
    title.length,
  ]);

  const onDelete = useCallback(() => {
    deletePLUCategory({
      variables: {
        id: props.route.params.id,
      },
      onCompleted: props.navigation.goBack,
      onError: (e) => {
        console.log("e", e);
      },
    });
  }, [deletePLUCategory, props.navigation.goBack, props.route.params.id]);

  const deleteAlertTitle = intl.formatMessage({
    defaultMessage: "Are you sure you want to delete this category?",
    description: "PLUCategoryEdit delete alert title",
  });

  const deleteAlertMessage = intl.formatMessage({
    defaultMessage: "All the products in this category will be deleted.",
    description: "PLUCategoryEdit delete alert message",
  });

  const Alert = useAlert();

  const onBeginDelete = useCallback(() => {
    Alert.alert(deleteAlertTitle, deleteAlertMessage, [
      {
        onPress: onDelete,
        style: "destructive",
        text: intlTokens.Delete,
      },
      {
        onPress: () => {},
        style: "cancel",
        text: intlTokens.Cancel,
      },
    ]);
  }, [
    Alert,
    deleteAlertMessage,
    deleteAlertTitle,
    intlTokens.Cancel,
    intlTokens.Delete,
    onDelete,
  ]);

  return (
    <Box style={{ flex: 1 }} onLayout={onLayout}>
      <ScrollView
        style={{ flex: 1, marginBottom: keyboardBottomInset }}
        contentContainerStyle={{
          paddingVertical: layout.space.medium,
          paddingBottom: layout.space.xxlarge,
        }}
      >
        <PLUCategoryIdentifiers
          color={color}
          setColor={setColor}
          title={title}
          setTitle={setTitle}
          usedColors={props.usedColors}
        />
        {props.checkoutTeams.map((team, i) => (
          <Box spaceHorizontal="medium" key={team.id}>
            {i !== 0 ? <AHSeparator /> : null}
            <TeamSelectListItem
              isFirst={i === 0}
              isLast={i === props.checkoutTeams.length - 1}
              team={team}
              onPress={() => {
                if (filteredTeamIds.includes(team.id)) {
                  setFilteredTeamIds(
                    filteredTeamIds.filter((id) => id !== team.id)
                  );
                } else {
                  setFilteredTeamIds([...filteredTeamIds, team.id]);
                }
              }}
              selected={!filteredTeamIds.includes(team.id)}
            />
          </Box>
        ))}
        <Box spaceHorizontal="medium" spaceVertical="xxlarge">
          <Box color="surface" cornerRadius="medium" space="medium">
            <AHButton
              themeType="negative"
              title={intl.formatMessage({
                defaultMessage: "Delete category",
                description: "PLUCategoryEdit delete category button",
              })}
              onPress={onBeginDelete}
              loading={isDeleting}
            />
          </Box>
        </Box>
      </ScrollView>
    </Box>
  );
}
